import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Settings, AlertTriangle, Globe, LogOut, FileText, LayoutDashboard, Server, Rocket, Crosshair, Users, Eye, Brain, Menu, X,
} from 'lucide-react';
import { GridPulses } from './GridPulses';
import { useAuth } from '../lib/auth-context';
import { LiteTierBanner } from './LiteTierBanner';
import { TenantSwitcher } from './TenantSwitcher';

// Primary navigation - core features
const PRIMARY_NAV = [
  { to: '/overview', label: 'Overview', icon: LayoutDashboard },
  { to: '/threats', label: 'Threats', icon: AlertTriangle },
  { to: '/actors', label: 'Actors', icon: Users },
  { to: '/intelligence', label: 'Intelligence', icon: Brain },
  { to: '/deceptions', label: 'Deceptions', icon: Eye },
  { to: '/canaries', label: 'Canaries', icon: Crosshair },
  { to: '/reports', label: 'Reports', icon: FileText },
];

// Secondary navigation - tools and settings
const SECONDARY_NAV = [
  { to: '/quickstart', label: 'Quickstart', icon: Rocket },
  { to: '/threat-map', label: 'Attack origins', icon: Globe },
  { to: '/agents', label: 'Agents', icon: Server },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function Layout() {
  const { disconnect, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    disconnect();
    navigate('/login');
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      isActive
        ? 'bg-blue-600/10 text-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.15)]'
        : 'text-gray-300 hover:text-white hover:bg-white/5'
    }`;

  const secondaryLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
      isActive
        ? 'bg-blue-600/10 text-blue-400 font-medium shadow-[0_0_8px_rgba(59,130,246,0.1)]'
        : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
    }`;

  const sidebarInner = (
    <>
      <div className="p-5 sm:p-6 border-b border-white/5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-[0_0_15px_rgba(37,99,235,0.4)] shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-lg text-white truncate">ShadowGuard</h1>
            <p className="text-xs text-gray-500">by Scafu</p>
          </div>
        </div>
        <button
          type="button"
          className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-1 mb-6">
          {PRIMARY_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={navLinkClass}>
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>

        <div className="border-t border-sg-border pt-4 space-y-1">
          <div className="text-xs text-gray-500 uppercase tracking-wide px-3 mb-2 font-semibold">
            Tools
          </div>
          {SECONDARY_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={secondaryLinkClass}>
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="px-4 pb-3 border-t border-sg-border">
        <div className="mt-3">
          <TenantSwitcher />
        </div>
      </div>

      <div className="p-4 border-t border-sg-border space-y-3">
        {tenant && (
          <div className="px-3 py-2 bg-sg-card rounded-lg">
            <p className="text-xs text-gray-500">Logged in as</p>
            <p className="text-sm text-white font-medium truncate">{tenant.name}</p>
            <p className="text-xs text-gray-400 capitalize">{tenant.tier} tier</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-sg-card rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <>
      <GridPulses />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(12,15,20,1)_0%,rgba(5,6,8,1)_100%)] pointer-events-none z-0" />

      <div className="flex h-[100dvh] min-h-0 relative z-10">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 shrink-0 bg-black/20 backdrop-blur-xl border-r border-white/5 flex-col relative z-10">
          {sidebarInner}
        </aside>

        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              aria-label="Close menu overlay"
              onClick={() => setMobileNavOpen(false)}
            />
            <aside className="absolute left-0 top-0 bottom-0 w-[min(18rem,88vw)] bg-[#0a0c10] border-r border-white/10 flex flex-col shadow-2xl">
              {sidebarInner}
            </aside>
          </div>
        )}

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Mobile top bar */}
          <header className="lg:hidden flex items-center gap-3 px-3 py-3 border-b border-white/5 bg-black/30 backdrop-blur-xl shrink-0">
            <button
              type="button"
              className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">ShadowGuard</p>
                {tenant && (
                  <p className="text-[11px] text-gray-500 truncate">{tenant.name}</p>
                )}
              </div>
            </div>
          </header>

          <LiteTierBanner />

          <main className="flex-1 overflow-auto overscroll-contain min-h-0">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
